import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  PauseIcon, 
  ArrowLeftIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon
} from '@heroicons/react/24/solid';
import { useAudio } from '../contexts/AudioContext';

const SessionPlayer: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const {
    currentSession,
    currentShow,
    currentEpisode,
    currentChannel,
    currentSegments,
    currentPanelists,
    isPlaying,
    isMuted,
    currentSegmentIndex,
    currentPartIndex,
    currentSpeaker,
    play,
    pause,
    stop,
    toggleMute,
    nextSegment,
    previousSegment,
    setCurrentSegment,
    totalDuration
  } = useAudio();

  const [captionsCollapsed, setCaptionsCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If we have a sessionId but no current session, we need to load it
    if (sessionId && !currentSession) {
      // For now, just show loading - in a real app, you'd load the session data here
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [sessionId, currentSession]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Loading Your Show...</h1>
          <p className="text-gray-600 dark:text-gray-400">Preparing your AI news experience.</p>
        </div>
      </div>
    );
  }

  if (!currentSession || !currentShow) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Active Session</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Start playing an episode to view the full player.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
          >
            Browse Episodes
          </button>
        </div>
      </div>
    );
  }

  const currentSegment = currentSegments[currentSegmentIndex];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{currentSession.sessionName}</h1>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  {currentEpisode && currentChannel && (
                    <div className="flex items-center space-x-2">
                      <span className="text-purple-600 dark:text-purple-400 font-medium">{currentChannel.name}</span>
                      <span>•</span>
                      <span className="text-blue-600 dark:text-blue-400 font-medium">{currentShow.name}</span>
                      <span>•</span>
                      <span className="text-green-600 dark:text-green-400 font-medium">{currentEpisode.title}</span>
                    </div>
                  )}
                  <p>
                    Segment {currentSegmentIndex + 1} of {currentSegments?.length || 0} • {formatTime(totalDuration)}
                  </p>
                </div>
                {currentSession.status === 'completed' && (
                  <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                    ✓ Session Completed
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* TV Screen View */}
          <div className="lg:col-span-2">
            <div className="tv-screen aspect-video relative overflow-hidden">
              {/* Panelists Grid */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="grid grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-16">
                  {currentPanelists?.slice(0, 4).map((panelist, index) => (
                    <div
                      key={panelist._id}
                      className={`relative ${
                        currentSpeaker === panelist._id 
                          ? 'panelist-avatar-speaking' 
                          : 'panelist-avatar'
                      }`}
                    >
                      <div className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                        {(panelist as any).headshotUrl ? (
                          <img
                            src={`/api/panelists/${panelist._id}/headshot`}
                            alt={panelist.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const fallback = target.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-full h-full flex items-center justify-center text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold"
                          style={{ display: (panelist as any).headshotUrl ? 'none' : 'flex' }}
                        >
                          {panelist.name.charAt(0)}
                        </div>
                      </div>
                      <div className="absolute -bottom-8 sm:-bottom-10 md:-bottom-12 lg:-bottom-16 xl:-bottom-20 left-1/2 transform -translate-x-1/2 text-center">
                        <p className="text-gray-900 dark:text-white text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-medium">{panelist.name}</p>
                        <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">{panelist.expertise?.[0] || 'General'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* No Segments Message */}
              {(!currentSegments || currentSegments.length === 0) && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-gray-900 dark:text-white text-lg font-medium mb-2">No Discussion Segments Available</div>
                    <div className="text-gray-600 dark:text-gray-300 text-sm">
                      This show doesn't have any discussion segments yet. Please try another show.
                    </div>
                    <button
                      onClick={() => navigate('/channels')}
                      className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Browse Channels
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Captions Section */}
            {currentSegment && (
              <div className="mt-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200">
                  {/* Captions Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Part {currentPartIndex + 1} of {currentSegment.scriptParts?.length || 1}
                      </span>
                    </div>
                    <button
                      onClick={() => setCaptionsCollapsed(!captionsCollapsed)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title={captionsCollapsed ? "Expand captions" : "Collapse captions"}
                    >
                      <svg 
                        className={`w-4 h-4 transition-transform duration-200 ${captionsCollapsed ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Captions Content */}
                  {!captionsCollapsed && (
                    <div className="p-4">
                      <div className="caption-text">
                        {currentSegment.scriptParts && currentSegment.scriptParts.length > 0 ? (
                          <div className="part-caption">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                {currentSegment.scriptParts[currentPartIndex].panelistName.charAt(0)}
                              </div>
                              <span className="font-semibold text-primary-600 dark:text-primary-400 text-lg">
                                {currentSegment.scriptParts[currentPartIndex].panelistName}
                              </span>
                            </div>
                            <div className="text-sm leading-relaxed">
                              {currentSegment.scriptParts[currentPartIndex].script}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm leading-relaxed">
                            {currentSegment.script || 'No script available for this segment.'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Controls Panel */}
          <div className="space-y-6">
            {/* Audio Controls */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
              <div className="space-y-3">
                {/* Play/Pause Button */}
                <button
                  onClick={isPlaying ? pause : play}
                  disabled={!currentSegment}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium py-2 px-3 rounded-lg flex items-center justify-center space-x-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isPlaying ? (
                    <>
                      <PauseIcon className="w-4 h-4" />
                      <span>Pause</span>
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-4 h-4" />
                      <span>Play</span>
                    </>
                  )}
                </button>

                {/* Mute Button */}
                <button
                  onClick={toggleMute}
                  className="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-3 rounded-lg flex items-center justify-center space-x-2 transition-colors duration-200 text-sm"
                >
                  {isMuted ? (
                    <>
                      <SpeakerXMarkIcon className="w-4 h-4" />
                      <span>Unmute</span>
                    </>
                  ) : (
                    <>
                      <SpeakerWaveIcon className="w-4 h-4" />
                      <span>Mute</span>
                    </>
                  )}
                </button>

                {/* Stop Button */}
                <button
                  onClick={stop}
                  className="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-3 rounded-lg flex items-center justify-center space-x-2 transition-colors duration-200 text-sm"
                >
                  <span>Stop</span>
                </button>

                {/* Navigation Buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={previousSegment}
                    disabled={currentSegmentIndex === 0}
                    className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium py-2 px-3 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Previous
                  </button>
                  <button
                    onClick={nextSegment}
                    disabled={currentSegmentIndex === currentSegments.length - 1}
                    className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium py-2 px-3 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            {/* Playlist */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Playlist</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {currentSegments.map((segment, index) => (
                  <button
                    key={segment._id}
                    onClick={() => setCurrentSegment(index)}
                    className={`w-full text-left p-3 rounded-lg transition-colors duration-200 ${
                      index === currentSegmentIndex
                        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="font-medium text-sm">{segment.title}</div>
                    <div className="text-xs opacity-75">
                      {segment.audioDuration ? formatTime(segment.audioDuration) : 'No duration'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionPlayer;